import { findNearMatches, foldAccents, normaliseName } from "@corklog/shared";

/**
 * What a reader's own cellars already hold from the producers an area report
 * names. Decided 2026-09-28 in `project-scope.md`: **the application does
 * this, never the model**, which is told nothing about any cellar. A mark is
 * drawn from the cellar's own record, so it cannot name a wine that is not
 * there.
 *
 * Matched with the rules the rest of the application already uses to say two
 * names are one house: the same after case, spaces and accents, or the same
 * by `findNearMatches` — *Poderi Aldo Conterno* is *Aldo Conterno*. Not by
 * the looser similarity `findNearMatches` also offers, which proposes and
 * does not decide: a mark on the page is a claim, and a claim that *Gosset*
 * is *Gosset-Brabant* would be wrong.
 */

/**
 * One of the reader's producers that a report's producer answers to: its own
 * name as the cellar spells it, how many wines, where, and the link.
 */
export type HeldMatch = {
  readonly name: string;
  readonly wines: number;
  readonly cellar: string;
  readonly to: string;
};

/**
 * What the reader's cellars hold from one producer in a report — **every
 * match, each with its own count and link**, since a house can be held under
 * its own name and under a cuvée's: *Moët & Chandon* two wines, *Dom Pérignon*
 * three. One summed number would link to a search that finds only some of
 * them.
 */
export type HeldMark = readonly HeldMatch[];

/** The key a report's producer is looked up by in the marks. */
export function producerKey(name: string): string {
  return foldAccents(normaliseName(name));
}

/** One of the reader's cellars, with its producers as `Producenter` lists them. */
export type CellarProducers = {
  readonly cellarId: string;
  readonly cellarName: string;
  readonly producers: readonly { readonly name: string; readonly uses: number }[];
};

/** A report's producer, with the cuvées the report names under it. */
export type ReportProducer = {
  readonly name: string;
  readonly cuvees: readonly string[];
};

/**
 * The marks for every producer in a report, keyed by `producerKey` of its
 * name.
 *
 * **The house is found under its own name and under each of its cuvées**,
 * because a cellar registers what the label says loudest: a bottle of Dom
 * Pérignon is as likely to stand under *Dom Pérignon* as under *Moët &
 * Chandon*. Each name is matched by the same rules — case, spaces and
 * accents, or `findNearMatches`' same-name rule — in every cellar the reader
 * reaches, in the order `/api/cellars` returns them.
 */
export function heldMarks(
  producers: readonly ReportProducer[],
  cellars: readonly CellarProducers[],
): Map<string, HeldMark> {
  const marks = new Map<string, HeldMark>();
  for (const producer of producers) {
    const key = producerKey(producer.name);
    if (marks.has(key)) continue;
    const matches: HeldMatch[] = [];
    for (const cellar of cellars) {
      const held = cellar.producers.filter((p) => p.uses > 0);
      const names = held.map((p) => p.name);
      const found = new Set<string>();
      for (const wanted of [producer.name, ...producer.cuvees]) {
        const match =
          names.find((candidate) => producerKey(candidate) === producerKey(wanted)) ??
          findNearMatches(wanted, names).find(
            (near) => near.reason === "same-name-different-spelling",
          )?.value;
        if (match !== undefined) found.add(match);
      }
      for (const name of found) {
        matches.push({
          name,
          wines: held.find((p) => p.name === name)?.uses ?? 0,
          cellar: cellar.cellarName,
          to: `/kallare/${encodeURIComponent(cellar.cellarId)}?sok=${encodeURIComponent(name)}`,
        });
      }
    }
    if (matches.length > 0) marks.set(key, matches);
  }
  return marks;
}
